'use client';

import { Fragment, useEffect, useRef } from 'react';
import './styles/@theme';
import { useRouter } from 'next/navigation';
import { DotsStepper } from './dots-stepper';
import { AnimationAsset } from '@/app/components/animation-asset/animation-asset';
import { getText } from '@/utils/text/text';
import { getPathFromLink } from '@/logic/page-config/navigation';
import { useStore } from '@/store/store';
import { getDybModalCTAClickedProps, getDybPageName } from '@/tracking/amplitude/events/general/jackpots';
import type { Page } from './types';
import type { SiteWideJackpotSlicePrimary } from '@/types/zones/slices/site-wide-jackpot-slice';

type DYBPageProps = {
    pageData: Page;
    currentPage: number;
    setNextPage: () => void;
    setPrevPage: () => void;
    totalNumberOfSteps: number;
    onClose: () => void;
    swjCategory?: SiteWideJackpotSlicePrimary['category'];
};

export const DYBPage = ({
    pageData,
    currentPage,
    setNextPage,
    setPrevPage,
    totalNumberOfSteps,
    onClose,
    swjCategory
}: DYBPageProps) => {
    const router = useRouter();
    const { sendEvent } = useStore((state) => state.tracking);
    const xIconUrl = '/images/icon-close-white-thin.svg';
    const isLastPage = totalNumberOfSteps === currentPage;
    const isFirstPage = 1 === currentPage;

    const linkedCategory = swjCategory
        ? getPathFromLink({
              type: 'category',
              uid: swjCategory.uid || '',
              lang: 'en',
              id: '',
              link_type: 'Document',
              key: ''
          })
        : undefined;

    const imageWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const wrapper = imageWrapperRef.current;
        if (!wrapper) return;

        const patchSvg = (svg: SVGSVGElement) => {
            svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
            svg.setAttribute('width', '100%');
            svg.setAttribute('height', '100%');
        };

        const svg = wrapper.querySelector('svg');
        if (svg) patchSvg(svg);

        const observer = new MutationObserver(() => {
            const el = wrapper.querySelector('svg');
            if (el) patchSvg(el);
        });

        observer.observe(wrapper, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [pageData.IMAGE]);

    const handleClickPlay = () => {
        sendEvent([
            {
                destination: 'amplitude',
                eventProps: getDybModalCTAClickedProps({
                    linkText: 'play_jackpot_games',
                    pageName: getDybPageName(currentPage),
                    ...(linkedCategory ? { linkUrl: linkedCategory } : {})
                })
            }
        ]);
        if (linkedCategory) {
            router.push(linkedCategory);
        }
        onClose();
    };

    const handleNextButton = () => {
        sendEvent([
            {
                destination: 'amplitude',
                eventProps: getDybModalCTAClickedProps({ linkText: 'next', pageName: getDybPageName(currentPage) })
            }
        ]);
        setNextPage();
    };

    const handleBackButton = () => {
        sendEvent([
            {
                destination: 'amplitude',
                eventProps: getDybModalCTAClickedProps({ linkText: 'back', pageName: getDybPageName(currentPage) })
            }
        ]);
        setPrevPage();
    };

    const handleSkipButton = () => {
        sendEvent([
            {
                destination: 'amplitude',
                eventProps: getDybModalCTAClickedProps({ linkText: 'skip', pageName: getDybPageName(currentPage) })
            }
        ]);
        onClose();
    };

    const handleOnClose = () => {
        sendEvent([
            {
                destination: 'amplitude',
                eventProps: getDybModalCTAClickedProps({ linkText: 'x_clicked', pageName: getDybPageName(currentPage) })
            }
        ]);
        onClose();
    };

    const backButton = () => (
        <button
            className={`dyb-pg-back-button-${isLastPage ? 'last' : 'mid'}`}
            onClick={handleBackButton}
            type="button">
            {getText({ text: 'components.siteWideJackpots.doubleYourBet.back' })}
        </button>
    );

    const nextButton = () => (
        <button
            className={`dyb-pg-next-button-${isFirstPage ? 'first' : 'mid'}`}
            onClick={handleNextButton}
            type="button">
            {getText({ text: 'components.siteWideJackpots.doubleYourBet.next' })}
        </button>
    );

    const playGamesButton = () => (
        <button className="dyb-pg-play-button" onClick={handleClickPlay} type="button">
            {getText({ text: 'components.siteWideJackpots.doubleYourBet.playJackpotGames' })}
        </button>
    );

    const buildPage = () => {
        switch (currentPage) {
            case 1:
                return <Fragment>{nextButton()}</Fragment>;
            case totalNumberOfSteps:
                return (
                    <Fragment>
                        {backButton()}
                        {playGamesButton()}
                    </Fragment>
                );
            default:
                return (
                    <Fragment>
                        {backButton()}
                        {nextButton()}
                    </Fragment>
                );
        }
    };

    return (
        <div className="dyb-pg-wrapper">
            <button type="button" className="dyb-pg-x-button" onClick={handleOnClose} aria-label="Close modal">
                <img src={xIconUrl} alt="" aria-hidden="true" />
            </button>
            <div className="dyb-pg-image-wrapper" ref={imageWrapperRef}>
                <AnimationAsset url={pageData.IMAGE || ''} loop autoplay />
            </div>
            <div className="dyb-pg-lower-half-wrapper">
                <DotsStepper currentStep={currentPage} totalNumberOfSteps={totalNumberOfSteps} />
                <div className="dyb-pg-text">
                    <div className="dyb-pg-title-row">
                        {pageData.LABEL && <span className="dyb-pg-label">{pageData.LABEL}</span>}
                        <h3 className="dyb-pg-title">{pageData.TITLE}</h3>
                    </div>
                    <p className={`dyb-pg-description-${currentPage}`}>{pageData.DESCRIPTION}</p>
                </div>
                <div className="dyb-pg-buttons">
                    <div className="dyb-pg-main-buttons">{buildPage()}</div>
                    <div className="dyb-pg-bottom">
                        {!isLastPage && (
                            <button className="dyb-pg-skip-button" onClick={handleSkipButton} type="button">
                                {getText({ text: 'components.siteWideJackpots.doubleYourBet.skipTour' })}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
